import qs from "qs-lite";
import { useMutation, useQuery, useQueryClient } from "react-query";
import api from "../../utils/api";
import { useHubSnackBar } from "../useSnackBar";
import { invalidate } from "../../helpers/clientHelper";

const WORKS = "/works";
const ORDERS = "/orders";
const SCHEDULE = "/schedule";
const SCHEDULE_THROUGHPUT = "/schedule/throughput";

const getWorkOrderDips = ({ queryKey }) => {
  const [path] = queryKey;
  return api.get(path);
};
export const useGetWorkOrderDips = (id) => {
  return useQuery([`/dips/${id}`], getWorkOrderDips, {
    retry: false,
    enabled: !!id,
  });
};

const getSearchWorkOrders = ({ queryKey }) => {
  const query = qs.stringify(queryKey[1]);
  const path = queryKey[1] ? `${WORKS}/search?${query}` : `${WORKS}/search`;
  return api.get(path);
};
export const useGetSearchWorkOrders = (search, workOrder) => {
  return useQuery(
    [WORKS, { search, workOrder }, "search"],
    getSearchWorkOrders,
    {
      enabled: !!search || !!workOrder,
    },
  );
};

const getWorkOrders = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${WORKS}/admin?${queryString}`);
};
export const useGetWorkOrders = () => {
  const query = new URLSearchParams(window.location.search);

  const location = query.get("location") || 1;

  const orderBy = query.get("orderBy") || "startDate";
  const sortOrder = query.get("sortOrder") || "desc";
  const limit = query.get("limit") || 50;
  const offset = query.get("offset") || 0;

  const printed = query.get("printed") ? query.get("printed") === "true" : null;
  const departments = query.get("departments");
  const startDate = query.get("startDate");
  const createdAt = query.get("createdAt");

  const queryProps = {
    location,
    limit,
    offset,
    orderBy,
    sortOrder,
    ...(query.get("printed") && { printed }),
    ...(departments && { departments }),
    ...(startDate && { startDate }),
    ...(createdAt && { createdAt }),
  };

  return useQuery([WORKS, { ...queryProps }], getWorkOrders, {
    retry: false,
  });
};

const getWorkOrderRouting = ({ queryKey }) => {
  const [path] = queryKey;
  return api.get(path);
};
export const useGetWorkOrderRouting = (workOrder) => {
  return useQuery([`${WORKS}/routing/${workOrder}`], getWorkOrderRouting, {
    retry: false,
    enabled: !!workOrder,
  });
};

const getWorkOrderByOrderId = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${WORKS}/order/${queryKey[2]}?${queryString}`);
};
export const useGetWorkOrderByOrderId = (id) => {
  const query = new URLSearchParams(window.location.search);

  const orderBy = query.get("orderBy") || "workOrder";
  const sortOrder = query.get("sortOrder") || "desc";
  const limit = query.get("limit") || 25;
  const offset = query.get("offset") || 0;

  const queryProps = {
    limit,
    offset,
    orderBy,
    sortOrder,
  };

  return useQuery([WORKS, queryProps, id], getWorkOrderByOrderId, {
    retry: false,
    enabled: !!id,
  });
};

const getWorkOrderLaborData = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${WORKS}/labor?${queryString}`);
};
export const useSearchWorkOrders = () => {
  const query = new URLSearchParams(window.location.search);

  const location = 1;
  const department = query.get("department");
  const work = query.get("work");

  const orderBy = query.get("orderBy") || "plannedStartDate";
  const sortOrder = query.get("sortOrder") || "asc";
  const limit = query.get("limit") || 100;
  const offset = query.get("offset") || 0;

  const queryProps = {
    location,
    limit,
    offset,
    orderBy,
    sortOrder,
    ...(department ? { department } : {}),
    ...(work ? { work } : {}),
  };

  return useQuery([WORKS, { ...queryProps }], getWorkOrderLaborData, {
    retry: false,
  });
};

export const getWorkOrderData = async ({ queryKey }) => {
  const [_, id, isId] = queryKey;
  const query = isId ? "?isId=true" : "";
  return api.get(`${WORKS}/${id}${query}`);
};
export const useGetWorkOrderData = (id, enabled = true, isId) => {
  return useQuery([WORKS, id, isId], getWorkOrderData, {
    enabled: !!id && enabled,
    retry: false,
  });
};

export const getWorkOrderSerializationData = async ({ queryKey }) => {
  const [_, id] = queryKey;
  return api.get(`${WORKS}/serialization/${id}`);
};
export const useGetWorkOrderSerialization = (id, enabled = true) => {
  return useQuery([WORKS, id, "serialization"], getWorkOrderSerializationData, {
    enabled: !!id && enabled,
    retry: false,
  });
};

const getWorkOrderFloorInstructions = ({ queryKey }) => {
  const [_, routeId] = queryKey;
  return api.get(`${WORKS}/routing/${routeId}/floor-instructions`);
};
export const useGetWorkOrderFloorInstructions = (routeId) => {
  return useQuery(
    [WORKS, routeId, "floor-instructions"],
    getWorkOrderFloorInstructions,
    { enabled: !!routeId, retry: false },
  );
};

const getQuickWorkOrderData = async ({ queryKey }) => {
  // eslint-disable-next-line
  const [_, id] = queryKey;
  return api.get(`${WORKS}/quick/${id}`);
};

export const useGetQuickWorkOrderData = (id) => {
  return useQuery([WORKS, id, "quick"], getQuickWorkOrderData, {
    enabled: !!id,
  });
};

const putAdminUpdateWorkOrder = (payload) => {
  const id = payload.id;
  delete payload.id;
  return api.put(`${WORKS}/${id}`, payload);
};
export const usePutAdminUpdateWorkOrder = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(putAdminUpdateWorkOrder, {
    onSuccess: () => {
      invalidate(queryClient, WORKS);
      addSnackbarSuccess({ message: "Successfully Updated Work Order" });
    },
  });
};

const putScanEmployeeUpdateWorkOrder = (payload) => {
  const id = payload.id;
  const scanPayload = {
    scannedUserId: payload.scannedUserId,
    updatedLabor: payload.updatedLabor,
  };

  return api.put(`${WORKS}/scan/${id}`, scanPayload);
};
export const usePutScanEmployeeUpdateWorkOrder = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(putScanEmployeeUpdateWorkOrder, {
    onSuccess: () => {
      invalidate(queryClient, WORKS);
      addSnackbarSuccess({ message: "Successfully Updated Work Order" });
    },
  });
};

const putAdminQuickUpdateWorkOrder = (payload) => {
  const id = payload.id;
  delete payload.id;
  return api.put(`${WORKS}/quick/${id}`, payload);
};
export const usePutAdminQuickUpdateWorkOrder = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(putAdminQuickUpdateWorkOrder, {
    onSuccess: () => {
      invalidate(queryClient, SCHEDULE);
      invalidate(queryClient, SCHEDULE_THROUGHPUT);
      addSnackbarSuccess({ message: "Successfully Updated Work Order" });
    },
  });
};

const putAdminCloseWorkOrder = (payload) => {
  const id = payload.id;
  delete payload.id;
  return api.put(`${WORKS}/close/${id}`, payload);
};
export const usePutAdminCloseWorkOrder = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(putAdminCloseWorkOrder, {
    onSuccess: () => {
      invalidate(queryClient, WORKS);
      invalidate(queryClient, SCHEDULE);
      addSnackbarSuccess({ message: "Successfully Closed Work Order" });
    },
  });
};

const putPrintWorkOrder = (id) => {
  return api.put(`${WORKS}/printed/${id}`);
};
export const usePutPrintWorkOrder = () => {
  const queryClient = useQueryClient();

  return useMutation(putPrintWorkOrder, {
    onSuccess: () => {
      invalidate(queryClient, WORKS);
    },
  });
};

///batch-create/line/:id
const postWorkOrderBatchesForLine = (data) =>
  api.post(`${WORKS}/batch-create/line/${data.lineId}`, data);
export const usePostWorkOrderBatchesForLine = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(postWorkOrderBatchesForLine, {
    onSuccess: () => {
      queryClient.invalidateQueries(`${ORDERS}/lines/works`);
      addSnackbarSuccess({ message: "Successfully Created Work Orders" });
    },
  });
};

const deleteWorkOrder = (id) => api.delete(`${WORKS}/${id}`);
export const useDeleteWorkOrder = () => {
  const { addSnackbarSuccess } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(deleteWorkOrder, {
    onSuccess: () => {
      invalidate(queryClient, WORKS);
      invalidate(queryClient, SCHEDULE);
      addSnackbarSuccess({ message: "Successfully Deleted Work Order" });
    },
  });
};
